import React, { useEffect, useState } from 'react'
import ItemsApp, { IItemModel } from './ItemsApp'
import CategoriesApp, { ICategoryModel } from '../categories/CategoriesApp'
import DynamicForm, { IDynamicFormElement, ISingleFormElementData } from '../../components/Form/DynamicForm'
import GenerateForm from '../../components/Form/GenerateForm'
import GenerateIcon from '../../components/GenerateIcon'
import { error } from 'console'
import { handleRequestError } from '../../services/api'
import { useDispatch } from 'react-redux'
import { addNewForm, createOrUpdateElementData } from '../../components/Form/formsSlice'
import { IconButton, InputAdornment } from '@mui/material'

interface IItemAdditionalDataFormProps {
    item: IItemModel | null,
    handleUpdateSubmitCategoryTitle: (title: string) => void,
    customHandleOnChange?: (id: string, value: any) => void,
    customCategoryIdFieldName?: string,
}

export default function ItemAdditionalDataForm(props: IItemAdditionalDataFormProps) {

    const dispatch = useDispatch()

    const {
        item,
        handleUpdateSubmitCategoryTitle,
        customHandleOnChange,
        customCategoryIdFieldName,
    } = props

    const baseContainerClassName: string = "item-additional-info-container-form"
    const [containerClassName, setContainerClassName] = useState<string>(baseContainerClassName)    

    const [show, setShow] = useState<boolean>(false)
    const [categoryTitle, setCategoryTitle] = useState<string>("")

    const [enableAdditionalData, setEnableAdditionalData] = useState<boolean>(false)

    const itemsApp = new ItemsApp()
    const categoriesApp = new CategoriesApp()

    const [category, setCategory] = useState<ICategoryModel | null>(null)

    const [loading, setLoading] = useState<boolean>(false)

    const formName: string = "item_additional_data"
    const elemPrefix: string = itemsApp.elemPrefix

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(elemPrefix + name, elementsData).value

    const singleElemDataValueWithoutPrefix = (name: string) => DynamicForm.returnSingleFormElementData(name, elementsData).value

    const categoryId = !customCategoryIdFieldName ? singleElemDataValue("category_id") : singleElemDataValueWithoutPrefix(customCategoryIdFieldName)

    const additionalData = item ? itemsApp.serializeItemAdditionaDataKeys(item.additional_data) : {}

    /**
     * Load category
     */
    const loadCategory = () => {
        if (!categoryId){
            return;
        }

        if (category && category.id === categoryId){
            return;
        }

        setLoading(true)

        categoriesApp.singleInstanceDetails(categoryId)
            .then(response => {
                setCategory(response.data)
                setLoading(false)
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })
        
    }

    useEffect(() => {
        let className: string = baseContainerClassName

        if (enableAdditionalData){
            className = show ? baseContainerClassName + " active" : baseContainerClassName

        }else{
            className = baseContainerClassName + " disabled"
        }

        setContainerClassName(className)

    }, [show, categoryTitle, enableAdditionalData])

    useEffect(() => {
        if (!categoryId){
            setCategory(null)
        }else{
            loadCategory()
        }
    }, [categoryId])

    useEffect(() => {

    }, [item])

    useEffect(() => {
        if (category){

            const categoryTitle = category.title.toLocaleLowerCase()

            let formElementData: ISingleFormElementData[] = []
            let newFormName: string = formName + "_" + category.title

            handleUpdateSubmitCategoryTitle(categoryTitle)

            /**
             * TV Screens
             */
            if (itemsApp.additionalCategoriesDataList.tv_screens.some(word => categoryTitle.includes(word.toLowerCase()))){

                /**
                 * Register form
                 */
                dispatch(addNewForm({
                    name: newFormName,
                    elements: [
                        {
                            id: elemPrefix + "screen_size",
                            xs: 6,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">inch</InputAdornment>,
                            label: "Screen Size",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "resolution",
                            xs: 6,
                            type: "Select",
                            label: "Resolution",
                            fullWidth: true,
                            items: itemsApp.screenResolutions,
                            addNoneItem: true,
                        },
                        {
                            id: elemPrefix + "screen_type",
                            xs: 6,
                            type: "TextField",
                            label: "Screen Type",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "refresh_rate",
                            xs: 6,
                            type: "TextField",
                            fieldType: "number",
                            label: "Refresh rate",
                            fullWidth: true,
                            endAdornment: <InputAdornment position="end">Hz</InputAdornment>
                        },
                        {
                            id: elemPrefix + "hdmi_ports",
                            xs: 6,
                            type: "TextField",
                            fieldType: "number",
                            label: "HDMI Ports",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "os",
                            xs: 6,
                            type: "TextField",
                            label: "OS",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "smart_tv",
                            xs: 6,
                            type: "Checkbox",
                            label: "Smart TV",
                            fullWidth: true
                        },
                    ]
                }))

                /**
                 * Elements data
                 */
                formElementData = [
                    ...formElementData,
                    {
                        name: elemPrefix + "screen_size",
                        value: additionalData.screen_size ?? ""
                    },
                    {
                        name: elemPrefix + "resolution",
                        value: additionalData.resolution ?? ""
                    },
                    {
                        name: elemPrefix + "screen_type",
                        value: additionalData.screen_type ?? ""
                    },
                    {
                        name: elemPrefix + "refresh_rate",
                        value: additionalData.refresh_rate ?? ""
                    },
                    {
                        name: elemPrefix + "hdmi_ports",
                        value: additionalData.hdmi_ports ?? ""
                    },
                    {
                        name: elemPrefix + "smart_tv",
                        value: additionalData.smart_tv ?? false
                    },
                    {
                        name: elemPrefix + "os",
                        value: additionalData.os ?? ""
                    },
                ]

                setEnableAdditionalData(true)
            }

            /**
             * Media Storage
             */
            if (itemsApp.additionalCategoriesDataList.media_storage.some(word => categoryTitle.includes(word.toLowerCase()))){

                /**
                 * Register form
                 */
                dispatch(addNewForm({
                    name: newFormName,
                    elements: [
                        {
                            id: elemPrefix + "storage_type",
                            xs: 6,
                            type: "Select",
                            label: "Storage type",
                            fullWidth: true,
                            items: itemsApp.mediaStorageType,
                            addNoneItem: true,
                        },
                        {
                            id: elemPrefix + "capacity_gb",
                            xs: 6,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">GB</InputAdornment>,
                            label: "Capacity",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "read_speed",
                            xs: 6,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">MB/s</InputAdornment>,
                            label: "Read speed",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "write_speed",
                            xs: 6,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">MB/s</InputAdornment>,
                            label: "Write speed",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "is_encrypted",
                            xs: 6,
                            type: "Checkbox",
                            label: "Encrypted",
                            fullWidth: true
                        },
                    ]
                }))

                /**
                 * Elements data
                 */
                formElementData = [
                    ...formElementData,
                    {
                        name: elemPrefix + "storage_type",
                        value: additionalData.storage_type ?? ""
                    },
                    {
                        name: elemPrefix + "capacity_gb",
                        value: additionalData.capacity_gb ?? ""
                    },
                    {
                        name: elemPrefix + "read_speed",
                        value: additionalData.read_speed ?? ""
                    },
                    {
                        name: elemPrefix + "write_speed",
                        value: additionalData.write_speed ?? ""
                    },
                    {
                        name: elemPrefix + "is_encrypted",
                        value: additionalData.is_encrypted ?? false
                    },
                ]

                setEnableAdditionalData(true)

            }

            /**
             * Furniture
             */
            if (itemsApp.additionalCategoriesDataList.furniture.some(word => categoryTitle.includes(word.toLowerCase()))){

                /**
                 * Register form
                 */
                dispatch(addNewForm({
                    name: newFormName,
                    elements: [
                        {
                            id: elemPrefix + "code",
                            xs: 12,
                            type: "TextField",
                            fieldType: "text",
                            label: "Code",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "material",
                            xs: 6,
                            type: "Select",
                            label: "Material",
                            fullWidth: true,
                            items: itemsApp.materialTypes,
                            addNoneItem: true,
                        },
                        {
                            id: elemPrefix + "color",
                            xs: 6,
                            type: "Select",
                            label: "Color",
                            fullWidth: true,
                            items: itemsApp.furnitureColors,
                            addNoneItem: true,
                        },
                        {
                            id: elemPrefix + "length_mm",
                            xs: 4,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                            label: "Length",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "width_mm",
                            xs: 4,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                            label: "Width",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "height_mm",
                            xs: 4,
                            type: "TextField",
                            fieldType: "number",
                            endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                            label: "Height",
                            fullWidth: true
                        },
                        {
                            id: elemPrefix + "mounting_type",
                            xs: 6,
                            type: "Select",
                            label: "Mounting Type",
                            fullWidth: true,
                            items: itemsApp.mountingTypes,
                            addNoneItem: true,
                        },
                        {
                            id: elemPrefix + "features",
                            xs: 6,
                            type: "Select",
                            label: "Features",
                            fullWidth: true,
                            items: itemsApp.furnitureFeatures,
                            addNoneItem: true,
                        },
                    ]
                }))

                /**
                 * Elements data
                 */
                formElementData = [
                    ...formElementData,
                    {
                        name: elemPrefix + "material",
                        value: additionalData.material ?? ""
                    },
                    {
                        name: elemPrefix + "color",
                        value: additionalData.color ?? ""
                    },
                    {
                        name: elemPrefix + "code",
                        value: additionalData.code ?? ""
                    },
                    {
                        name: elemPrefix + "length_mm",
                        value: additionalData.length_mm ?? null
                    },
                    {
                        name: elemPrefix + "width_mm",
                        value: additionalData.width_mm ?? null
                    },
                    {
                        name: elemPrefix + "height_mm",
                        value: additionalData.height_mm ?? null
                    },
                    {
                        name: elemPrefix + "features",
                        value: additionalData.features ?? null
                    },
                    {
                        name: elemPrefix + "mounting_type",
                        value: additionalData.mounting_type ?? null
                    },
                ]

                setEnableAdditionalData(true)

            }

            /**
            * Update elements data
             */
            if (formElementData.length > 0){
                formElementData.map((elem) => dispatch(createOrUpdateElementData(elem)))
            }

        }else{
            setEnableAdditionalData(false)
        }
    }, [category])

    return(
        <div className={containerClassName}>
            <div className="content-of-item-additional-info-container-form">

                <div className="header-box-of-item-additional-info-container-form" onClick={() => {
                    if (!enableAdditionalData){
                        return
                    }

                    setShow(show ? false : true)
                }}>
                    <div className="content-of-header-box-of-item-additional-info-container-form">
                        <GenerateIcon icon="arrow-down" />
                        <h3>Additional Information</h3>
                    </div>
                </div>

                <div className="form-of-item-additional-info-container-form">
                    <div className="content-of-form-of-item-additional-info-container-form">
                        {category && <GenerateForm name={formName + "_" + category.title} customHandleOnChange={customHandleOnChange} />}
                    </div>
                </div>

            </div>
        </div>
    )
}